import { type CustomerListItem } from '@utazzo/shared';
import { UzAvatar, UzButton, UzInput, UzSpinner } from '@utazzo/ui';
import { Search, UserPlus } from 'lucide-react';
import { useState } from 'react';

import { useCustomerSearch } from '../use-lead-options';

interface CustomerPickerProps {
  value: CustomerListItem | null;
  onChange: (customer: CustomerListItem | null) => void;
  disabled?: boolean;
  required?: boolean;
  /**
   * When true the selected customer is shown but cannot be cleared or
   * changed - used in the lead-form's edit mode where the FK is the
   * canonical link and re-pointing would orphan history.
   */
  readOnly?: boolean;
  /** Optional "create new" link target - defaults to /customers/new. */
  newCustomerHref?: string;
  /** Optional filter to restrict the search to one customer kind. */
  kind?: 'INDIVIDUAL' | 'CORPORATE';
  error?: string;
}

/**
 * Inline search-and-pick widget for the lead form's customer field.
 *
 * The lead's `customerId` FK must reference an existing customer; for now we
 * don't auto-create one from the lead form (the legacy `UILeadCustomer`
 * find-or-create flow will land alongside `CustomersService.upsertByEmailOrPhone`
 * in a follow-up - see migration plan Phase A0). Until then operators search
 * for the customer or jump to the customer stepper to create one.
 *
 * UX: a debounce-free search (TanStack handles re-fetch), a result list as
 * `displayName · mobile · email` rows. Selection persists; "Change" clears
 * the selection and reopens the search.
 */
export const CustomerPicker = ({
  value,
  onChange,
  disabled,
  required,
  readOnly,
  newCustomerHref = '/customers/new',
  kind,
  error,
}: CustomerPickerProps): React.JSX.Element => {
  const [term, setTerm] = useState('');
  const { customers, isLoading, enabled } = useCustomerSearch(term, kind);

  if (value) {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
          Customer{required && <span className="text-red-500"> *</span>}
        </label>
        <div className="flex items-center gap-3 rounded-md border border-zinc-200 bg-white p-3">
          <UzAvatar name={value.displayName} size="md" />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium text-zinc-900">{value.displayName}</span>
            <span className="truncate text-xs text-zinc-500">
              {value.mobileNumber ?? '-'}
              {value.email && <> · {value.email}</>}
            </span>
          </div>
          {!readOnly && (
            <UzButton variant="ghost" size="sm" onClick={() => onChange(null)} disabled={disabled}>
              Change
            </UzButton>
          )}
        </div>
        {readOnly && (
          <p className="text-xs text-zinc-500">
            Customer can&apos;t be re-pointed after the lead is created - it would orphan the
            follow-up and stage history.
          </p>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
        Customer{required && <span className="text-red-500"> *</span>}
      </label>
      <UzInput
        name="customer-search"
        placeholder="Search by name, phone, or email (min 2 chars)"
        leftAdornment={<Search className="h-4 w-4" />}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        disabled={disabled}
        autoComplete="off"
      />
      {enabled && (
        <div className="max-h-64 overflow-y-auto rounded-md border border-zinc-200 bg-white">
          {isLoading ? (
            <div className="flex items-center gap-2 p-3 text-sm text-zinc-500">
              <UzSpinner size="sm" /> Searching…
            </div>
          ) : customers.length === 0 ? (
            <div className="p-3 text-sm text-zinc-500">
              No matches. Try a different search, or{' '}
              <a
                className="text-blue-600 hover:text-blue-700"
                href={newCustomerHref}
                target="_blank"
                rel="noreferrer"
              >
                create a new customer
              </a>
              .
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {customers.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 p-3 text-left hover:bg-zinc-50"
                    onClick={() => {
                      onChange(c);
                      setTerm('');
                    }}
                  >
                    <UzAvatar name={c.displayName} size="sm" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium text-zinc-900">
                        {c.displayName}
                      </span>
                      <span className="truncate text-xs text-zinc-500">
                        {c.mobileNumber ?? '-'}
                        {c.email && <> · {c.email}</>}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {!enabled && (
        <p className="text-xs text-zinc-500">
          No customer match? You can{' '}
          <a
            className="text-blue-600 hover:text-blue-700"
            href={newCustomerHref}
            target="_blank"
            rel="noreferrer"
          >
            <UserPlus className="-mt-0.5 inline h-3 w-3" /> create a new customer
          </a>{' '}
          and come back.
        </p>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};
